import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  App,
  Button,
  Empty,
  Flex,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { listVersions } from '@/api/versions'
import AddIcon from '@/assets/add.svg?react'
import BrandButton from '@/components/BrandButton'
import { useCurrentApp } from '@/hooks/useCurrentApp'
import { useAppStore, useUploadDrawerStore, useVersionDrawerStore } from '@/store'
import { useShallow } from 'zustand/react/shallow'
import type {
  AppVersion,
  Collaborator,
  PlatformFamily,
  VersionStatus,
} from '@/types'
import {
  ALL_PLATFORM_FAMILIES,
  PLATFORM_FAMILY_LABEL,
  hasEverReleased,
  platformChipStyle,
  platformLabel,
} from './versions/platform'
import { versionPlatformEntries } from './versions/platformEntries'
import {
  confirmVersionAction,
  isDangerAction,
  listActionsForStatus,
  type VersionListAction,
} from './versions/versionActions'
import { StatusTag } from './versions/StatusTag'
import { useConfirmDropUploads } from './versions/useConfirmDropUploads'

const PAGE_SIZE = 20
const EMPTY_VERSIONS: AppVersion[] = []
const EMPTY_COLLABORATORS: Collaborator[] = []

const STATUS_FILTER_OPTIONS: Array<VersionStatus | 'all'> = [
  'all',
  'developing',
  'reviewing',
  'rejected',
  'scheduled',
  'released',
  'delisted',
  'disabled',
  'archived',
  'rolling_back',
]

const NO_RELEASE_TIME = new Set<VersionStatus>([
  'developing',
  'reviewing',
  'rejected',
  'scheduled',
])

function formatMinute(iso: string | null | undefined): string {
  if (!iso) return '—'
  return dayjs(iso).format('YYYY-MM-DD HH:mm')
}

function PlatformChip({ platform }: { platform: string }) {
  const style = platformChipStyle(platform)
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 6px',
        borderRadius: 6,
        fontSize: 12,
        background: style.background,
        color: style.color,
        border: style.border,
      }}
    >
      {platformLabel(platform)}
    </span>
  )
}

function Versions() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const app = useCurrentApp()
  const openNewVersion = useUploadDrawerStore((s) => s.openNewVersion)
  const confirmDropUploads = useConfirmDropUploads()
  const openVersion = useVersionDrawerStore((s) => s.openVersion)
  const syncVersions = useAppStore((s) => s.syncVersions)
  const setMaxOnlineVersion = useAppStore((s) => s.setMaxOnlineVersion)
  const versionsListRev = useAppStore((s) => s.versionsListRev)
  const appVersions = useAppStore((s) => s.versions[app.id] ?? EMPTY_VERSIONS)
  const collaborators = useAppStore(
    (s) => s.collaborators[app.id] ?? EMPTY_COLLABORATORS,
  )
  const storeActions = useAppStore(
    useShallow((s) => ({
      deleteVersion: s.deleteVersion,
      withdrawVersion: s.withdrawVersion,
      cancelSchedule: s.cancelSchedule,
      releaseNow: s.releaseNow,
      delistVersion: s.delistVersion,
      disableVersion: s.disableVersion,
      enableVersion: s.enableVersion,
      relistVersion: s.relistVersion,
      rollbackVersion: s.rollbackVersion,
    })),
  )

  const [platformFilter, setPlatformFilter] = useState<PlatformFamily | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<VersionStatus | 'all'>('all')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [pageData, setPageData] = useState<AppVersion[]>([])
  const [hasCollaboratorsRemote, setHasCollaboratorsRemote] = useState(false)
  const [loading, setLoading] = useState(true)

  const has_collaborators =
    hasCollaboratorsRemote || collaborators.length > 1

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void listVersions(app.id, {
      platform: platformFilter === 'all' ? undefined : platformFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      page_size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return
        setPageData(res.list)
        setTotal(res.total)
        setHasCollaboratorsRemote(res.has_collaborators)
        syncVersions(app.id, res.list)
        setMaxOnlineVersion(app.id, res.max_online_version)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        message.error(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- syncVersions/message 不稳定
  }, [app.id, platformFilter, statusFilter, page, versionsListRev])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  /** 列表项只有浅字段；store 里同 id 版本覆盖状态与包字段，避免整表刷新后列表页短暂双线上 */
  const withDetail = (row: AppVersion): AppVersion => {
    const cached = appVersions.find((v) => v.id === row.id)
    if (!cached) return row
    return {
      ...row,
      status: cached.status,
      released_at: cached.released_at,
      scheduled_at: cached.scheduled_at,
      last_operated_at: cached.last_operated_at,
      main_package: cached.main_package ?? row.main_package,
      platform_packages: cached.platform_packages ?? row.platform_packages,
    }
  }

  const runAction = (action: VersionListAction, version: AppVersion) => {
    confirmVersionAction({
      t,
      modal,
      message,
      action,
      version: withDetail(version),
      app_id: app.id,
      store: storeActions,
      appVersions,
    })
  }

  const columnHeaders = [
    t('versions.version'),
    t('versions.platforms'),
    t('versions.status'),
    t('versions.onlineTime'),
    t('versions.lastUpdated'),
    ...(has_collaborators ? [t('versions.operator')] : []),
    t('versions.actions'),
  ]

  const platformTabs: Array<{ key: PlatformFamily | 'all'; label: string }> = [
    { key: 'all', label: t('versions.filterAll') },
    ...ALL_PLATFORM_FAMILIES.map((f) => ({ key: f, label: PLATFORM_FAMILY_LABEL[f] })),
  ]

  return (
    <div
      style={{
        background: '#fff',
        padding: 20,
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Flex align="center" justify="space-between" gap={10} style={{ flexWrap: 'nowrap' }}>
        <Flex align="center" gap={4} wrap="wrap" style={{ minWidth: 0, flex: 1 }}>
          {platformTabs.map((tab) => {
            const active = platformFilter === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setPlatformFilter(tab.key)
                  setPage(1)
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 12,
                  border: 'none',
                  background: active ? 'rgba(99,102,241,0.1)' : 'transparent',
                  color: active ? '#6366F1' : 'rgba(0,0,0,0.4)',
                  fontSize: 14,
                  fontWeight: active ? 500 : 400,
                  lineHeight: '22px',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </Flex>
        <Flex align="center" gap={10} style={{ flexShrink: 0 }}>
          <Select
            value={statusFilter}
            style={{ width: 140 }}
            onChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
            options={STATUS_FILTER_OPTIONS.map((s) => ({
              value: s,
              label:
                s === 'all'
                  ? t('versions.filterStatusAll')
                  : t(`versions.status_${s}` as const),
            }))}
          />
          <BrandButton
            icon={<AddIcon />}
            onClick={() => confirmDropUploads(() => openNewVersion(app.id))}
          >
            {t('versions.create')}
          </BrandButton>
        </Flex>
      </Flex>

      {!loading && total === 0 ? (
        <Empty
          style={{ marginTop: 80 }}
          description={t('versions.emptyGuide')}
        />
      ) : (
        <div style={{ marginTop: 16, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.04)' }}>
                {columnHeaders.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '14px 20px',
                      fontSize: 13,
                      fontWeight: 400,
                      color: '#000',
                      borderBottom: '1px solid #DCDCDC',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={columnHeaders.length}
                    style={{ padding: '80px 0', textAlign: 'center' }}
                  >
                    <Spin />
                  </td>
                </tr>
              ) : (
                pageData.map((row) => {
                const actions = listActionsForStatus(row.status, {
                  neverReleased: !hasEverReleased(row),
                })
                return (
                  <tr key={row.id} style={{ height: 52 }}>
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => openVersion(app.id, row.id)}
                        style={{
                          color: '#000',
                          fontFamily: 'ui-monospace, monospace',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        v{row.version}
                      </button>
                    </td>
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                      }}
                    >
                      <Space size={6} wrap>
                        {versionPlatformEntries(withDetail(row)).map(
                          ({ platform, reusedFrom }) => (
                            <Tooltip
                              key={platform}
                              title={
                                reusedFrom
                                  ? t('versions.reusedFrom', { version: reusedFrom })
                                  : undefined
                              }
                            >
                              <span style={{ opacity: reusedFrom ? 0.55 : 1 }}>
                                <PlatformChip platform={platform} />
                              </span>
                            </Tooltip>
                          ),
                        )}
                      </Space>
                    </td>
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                      }}
                    >
                      <StatusTag status={row.status} />
                    </td>
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                        fontSize: 13,
                        color: 'rgba(0,0,0,0.65)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {NO_RELEASE_TIME.has(row.status)
                        ? '—'
                        : formatMinute(row.released_at)}
                    </td>
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                        fontSize: 13,
                        color: 'rgba(0,0,0,0.65)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.status === 'scheduled' && row.scheduled_at
                        ? formatMinute(row.scheduled_at)
                        : formatMinute(row.last_operated_at)}
                    </td>
                    {has_collaborators && (
                      <td
                        style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #DCDCDC',
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.operator
                          ? `${row.operator.nickname} @${row.operator.username}`
                          : '—'}
                      </td>
                    )}
                    <td
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #DCDCDC',
                        minWidth: 160,
                      }}
                    >
                      {row.status === 'rolling_back' ? (
                        <Typography.Text type="secondary">
                          {t('versions.rollingBack')}
                        </Typography.Text>
                      ) : (
                        <Space size={4}>
                          {actions.map((action) => (
                            <Button
                              key={action}
                              type="text"
                              size="small"
                              danger={isDangerAction(action)}
                              onClick={() => runAction(action, row)}
                            >
                              {t(`versions.action_${action}` as const)}
                            </Button>
                          ))}
                        </Space>
                      )}
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
          </table>

          {!loading && totalPages > 1 && (
            <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
              <Button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.back')}
              </Button>
              <Typography.Text type="secondary">
                {page} / {totalPages}
              </Typography.Text>
              <Button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                →
              </Button>
            </Flex>
          )}
        </div>
      )}
    </div>
  )
}

export default Versions
